import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  IconButton,
  CircularProgress,
  Box,
  Alert,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import dayjs from '@/config/dayjs';
import { useLocalization } from '@/hooks/useLocalization';
import { DatePicker, DateValidationError } from '@mui/x-date-pickers';
import { PickerValue } from '@mui/x-date-pickers/internals';

export type MarkAsClosedPickerValue = PickerValue;

interface MarkAsClosedDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (closedDate: PickerValue) => void;
  accountName: string;
  isLoading?: boolean;
  errorText?: string | null;
}

const MarkAsClosedDialog = ({
  open,
  onClose,
  onConfirm,
  accountName,
  isLoading = false,
  errorText = null,
}: MarkAsClosedDialogProps) => {
  const { t } = useLocalization();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const today = dayjs();
  const [closedDate, setClosedDate] = useState<PickerValue>(today);
  const [error, setError] = React.useState<DateValidationError | null>(null);

  React.useEffect(() => {
    if (!open) {
      return;
    }
    setClosedDate(dayjs());
    setError(null);
  }, [open]);

  const handleConfirm = () => {
    onConfirm(closedDate);
  };

  const errorMessage = React.useMemo(() => {
    switch (error) {
      case 'maxDate': {
        return 'Cannot be a future date';
      }

      case 'invalidDate': {
        return 'Invalid Date';
      }

      default: {
        return '';
      }
    }
  }, [error]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth='sm'
      fullWidth
      fullScreen={isMobile}
      slotProps={{
        paper: {
          sx: {
            paddingTop:
              'var(--safe-area-inset-top, env(safe-area-inset-top, 0px))',
            paddingBottom:
              'var(--safe-area-inset-bottom, env(safe-area-inset-bottom, 0px))',
          },
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Typography
          component='h3'
          variant='h6'
        >
          {t('areYouSure')}
        </Typography>
        <IconButton
          aria-label={t('close')}
          onClick={onClose}
          size='small'
          disabled={isLoading}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        {errorText ? (
          <Alert
            severity='error'
            sx={{ mb: 2 }}
            data-testid='mark-as-closed-save-error'
          >
            {errorText}
          </Alert>
        ) : null}
        <Box sx={{ mb: 4 }}>
          <Typography
            variant='subtitle1'
            gutterBottom
          >
            {accountName
              ? t('MarkAsClosedDialog.closeAccountConfirmation', {
                  accountName: accountName,
                })
              : t('MarkAsClosedDialog.closeAccountConfirmationFallback')}
          </Typography>
          <Typography
            variant='body2'
            color='textSecondary'
            gutterBottom
          >
            {t('MarkAsClosedDialog.closeAccountDescription')}
          </Typography>
        </Box>

        <DatePicker
          label={t('MarkAsClosedDialog.closedAsOf')}
          value={closedDate}
          onChange={(value) => {
            setClosedDate(value);
          }}
          maxDate={dayjs()}
          onError={(error) => {
            setError(error);
          }}
          slotProps={{
            textField: {
              helperText: errorMessage,
              required: true,
            },
          }}
          sx={{
            '& .MuiFormHelperText-root.Mui-error': {
              margin: '4px 0 0 0',
            },
          }}
        />
      </DialogContent>
      <DialogActions>
        <Button
          size='large'
          variant='outlined'
          onClick={onClose}
          disabled={isLoading}
        >
          {t('cancel')}
        </Button>
        <Button
          size='large'
          onClick={handleConfirm}
          variant='contained'
          disabled={isLoading || !closedDate || !!error}
        >
          {isLoading ? <CircularProgress size={24} /> : t('ok')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default MarkAsClosedDialog;
